import { parseDollarsToCents, pricingFromEntry, type PricingMode } from '@sellit/shared';
import { centsToInput } from '../lib/format';

/** What the seller has entered: a price box plus the "Free" and "or best offer" toggles. */
export interface PriceValue {
  price: string;
  free: boolean;
  offers: boolean;
}

export function priceValueFrom(mode: PricingMode, cents: number | null): PriceValue {
  return { price: mode === 'FREE' ? '' : centsToInput(cents), free: mode === 'FREE', offers: mode === 'MAKE_OFFER' };
}

/** Reads the entry: `pricing` is null while a price is still needed or the price can't be read. */
export function readPrice(v: PriceValue) {
  const blank = v.free || v.price.trim() === '';
  const cents = blank ? null : parseDollarsToCents(v.price);
  const invalid = !blank && cents === null;
  return { invalid, pricing: invalid ? null : pricingFromEntry({ cents, free: v.free, offers: v.offers }) };
}

/**
 * Price-first entry: one box for the price, with "Free" and "or best offer" as optional taps.
 * Typing 0 also means free. Replaces the old Fixed / Make offer / Free switch; stored modes are the same.
 */
export function PriceEntry({ value, onChange, error }: { value: PriceValue; onChange: (v: PriceValue) => void; error?: string }) {
  const { invalid, pricing } = readPrice(value);
  const hint = value.free
    ? 'Listed as free.'
    : value.offers
      ? value.price.trim() === '' ? 'Buyers make you an offer.' : 'Buyers can offer less.'
      : null;
  return (
    <div className="field price-entry">
      <label htmlFor="price">Price</label>
      <div className="money">
        <span aria-hidden="true">$</span>
        <input
          id="price"
          inputMode="decimal"
          enterKeyHint="done"
          placeholder={value.free ? 'Free' : '0'}
          disabled={value.free}
          value={value.free ? '' : value.price}
          onChange={(e) => onChange({ ...value, price: e.target.value })}
        />
      </div>
      <div className="toggle-chips" role="group" aria-label="Price options">
        <button type="button" className="toggle-chip" aria-pressed={value.free} onClick={() => onChange({ ...value, free: !value.free, offers: false })}>
          Free
        </button>
        <button type="button" className="toggle-chip" aria-pressed={value.offers} onClick={() => onChange({ ...value, offers: !value.offers, free: false })}>
          or best offer
        </button>
      </div>
      {invalid && <small className="error">Enter a price like 40 or 12.50</small>}
      {error && <small className="error">{error}</small>}
      {hint && !invalid && <small className="muted">{hint}</small>}
      {!pricing && !invalid && !error && <small className="muted">Type a price, or tap Free.</small>}
    </div>
  );
}
